/**
 * SessionEnd - Light Theme Countdown Page
 * 
 * Eye-friendly design with:
 * - Clean white backgrounds
 * - Soft animations
 * - Muted accent colors
 */

import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { keyframes } from '@emotion/react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { clearSession } from '../reduxStore/WialonSessionReducer';
import Cookies from 'universal-cookie';
import { useTranslation } from 'react-i18next';
import '../styles/ModernStyles.css';

const cookies = new Cookies();

// Animation keyframes
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-12px) rotate(3deg); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

// Floating Shape Component - Light Theme
const FloatingShape = ({ size, top, left, right, bottom, delay, filled = false }) => (
  <Box
    sx={{
      position: 'absolute',
      top,
      left,
      right,
      bottom,
      width: size,
      height: size,
      borderRadius: filled ? '12px' : '50%',
      border: filled ? 'none' : '2px solid rgba(78, 205, 196, 0.3)',
      background: filled ? 'rgba(78, 205, 196, 0.05)' : 'transparent',
      opacity: 0.6,
      animation: `${float} ${6 + delay}s ease-in-out infinite`,
      animationDelay: `${delay}s`,
      transform: filled ? 'rotate(15deg)' : 'none',
      pointerEvents: 'none',
    }}
  />
);

export default function SessionEnd() {
  const [t] = useTranslation('common');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    // Clear session on mount
    dispatch(clearSession());

    // Countdown timer
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          // Redirect to host URL
          const hostUrl = cookies.get('hostUrl');
          if (hostUrl) {
            window.location.href = hostUrl;
          } else {
            navigate('/sorry', { replace: true });
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [dispatch, navigate]);

  const progress = ((5 - countdown) / 5) * 100;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #F8FAFC 0%, #EEF2F7 50%, #E2E8F0 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Floating Background Shapes */}
      <FloatingShape size={100} top="10%" left="10%" delay={0} />
      <FloatingShape size={60} top="20%" right="15%" delay={1.5} />
      <FloatingShape size={80} bottom="15%" left="8%" delay={2} />
      <FloatingShape size={50} bottom="20%" right="10%" delay={0.5} />
      <FloatingShape size={70} top="50%" left="5%" delay={1} filled />

      {/* Main Card */}
      <Box
        sx={{
          width: '90%',
          maxWidth: 420,
          background: '#FFFFFF',
          borderRadius: 4,
          padding: 5,
          border: '1px solid #E2E8F0',
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.08)',
          position: 'relative',
          zIndex: 1,
          animation: `${fadeInUp} 0.8s ease-out forwards`,
          textAlign: 'center',
        }}
      >
        {/* Countdown Circle */}
        <Box
          sx={{
            width: 120,
            height: 120,
            borderRadius: '50%',
            margin: '0 auto 24px',
            position: 'relative',
            background: 'transparent',
          }}
        >
          {/* Background circle */}
          <svg
            width="120"
            height="120"
            style={{ position: 'absolute', top: 0, left: 0, transform: 'rotate(-90deg)' }}
          >
            <circle
              cx="60"
              cy="60"
              r="54"
              fill="none"
              stroke="#E2E8F0"
              strokeWidth="6"
            />
            <circle
              cx="60"
              cy="60"
              r="54"
              fill="none"
              stroke="url(#gradient)"
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={2 * Math.PI * 54}
              strokeDashoffset={2 * Math.PI * 54 * (1 - progress / 100)}
              style={{ transition: 'stroke-dashoffset 0.5s ease' }}
            />
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#4ECDC4" />
                <stop offset="100%" stopColor="#6C9BCF" />
              </linearGradient>
            </defs>
          </svg>
          {/* Countdown number */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              animation: `${pulse} 1s ease-in-out infinite`,
            }}
          >
            <Typography
              sx={{
                fontSize: 40,
                fontWeight: 700,
                background: 'linear-gradient(135deg, #4ECDC4 0%, #6C9BCF 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontFamily: 'Cairo, sans-serif',
              }}
            >
              {countdown}
            </Typography>
          </Box>
        </Box>

        {/* Title */}
        <Typography
          sx={{
            fontSize: 22,
            fontWeight: 700,
            color: '#1E293B',
            fontFamily: 'Cairo, sans-serif',
            marginBottom: 1.5,
          }}
        >
          {t('GLOBAL.sessionEnding') || 'Session Ending'}
        </Typography>

        {/* Message */}
        <Typography
          sx={{
            fontSize: 14,
            color: '#64748B',
            fontFamily: 'Cairo, sans-serif',
            marginBottom: 3,
            lineHeight: 1.6,
          }}
        >
          {t('GLOBAL.endSessionMessage') || 'Your session has ended. Redirecting in'}{' '}
          <span style={{ color: '#4ECDC4', fontWeight: 600 }}>{countdown}</span>{' '}
          {t('GLOBAL.endSessionMessageUnits') || 'seconds'}
        </Typography>

        {/* Progress Bar */}
        <Box
          sx={{
            width: '100%',
            height: 6,
            background: '#E2E8F0',
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              height: '100%',
              background: 'linear-gradient(90deg, #4ECDC4 0%, #6C9BCF 100%)',
              borderRadius: 3,
              width: `${progress}%`,
              transition: 'width 0.5s ease',
            }}
          />
        </Box>
      </Box>

      {/* Footer */}
      <Typography
        sx={{
          position: 'absolute',
          bottom: 20,
          color: '#94A3B8',
          fontSize: 12,
          fontFamily: 'Cairo, sans-serif',
        }}
      >
        WenkGPS. All rights reserved 2026 ©
      </Typography>
    </Box>
  );
}
